import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  PlusIcon,
  TrashIcon,
  PencilIcon,
  CheckIcon,
  XMarkIcon,
  UserIcon,
  StarIcon,
  ArrowTrendingUpIcon,
  CalendarDaysIcon,
  LightBulbIcon,
  ArrowUpIcon,
  ArrowDownIcon,
} from '@heroicons/react/24/outline';

import type { 
  InsumosRelatoriosContent, 
  PerfilRelatorioConfig, 
  PontoForteConfig, 
  AreaDesenvolvimentoConfig,
  TarefaPlanoAcaoConfig,
} from '../../services/contentSnapshotService';
import type { ValidationError } from '../../services/contentAdminService';

interface ProfileEditorProps {
  versionId: string;
  content: any;
  onChange: (content: any) => void;
  validationErrors: ValidationError[];
}

type EditingSection = 'basic' | 'strengths' | 'development' | 'plan' | 'strategies' | null;
type PlanPeriod = '30_dias' | '90_dias' | '6_meses';

const planPeriodLabels: Record<PlanPeriod, string> = {
  '30_dias': '30 Dias',
  '90_dias': '90 Dias',
  '6_meses': '6 Meses',
};

export const ProfileEditor: React.FC<ProfileEditorProps> = ({
  versionId,
  content,
  onChange,
  validationErrors,
}) => {
  const [selectedProfile, setSelectedProfile] = useState<string>('');
  const [editingSection, setEditingSection] = useState<EditingSection>(null);
  const [activePlanPeriod, setActivePlanPeriod] = useState<PlanPeriod>('30_dias');

  const reportContents: InsumosRelatoriosContent = content.report_contents || {
    versao: '1.0',
    data_criacao: new Date().toISOString(),
    descricao: 'Conteúdo dos relatórios de perfis',
    estrutura_relatorio: { secoes: [] },
    perfis: {},
    templates_relatorio: {
      introducao: '',
      perfil_secundario_explicacao: '',
      conclusao: '',
    },
  };

  const updateReportContents = (newContents: InsumosRelatoriosContent) => {
    onChange({
      ...content,
      report_contents: newContents,
    });
  };

  const profiles = Object.keys(reportContents.perfis);
  const currentProfile = selectedProfile ? reportContents.perfis[selectedProfile] : null;

  // Profile creation
  const handleCreateProfile = () => {
    const newProfileId = prompt('ID do novo perfil (ex: equilibrada):');
    if (!newProfileId || profiles.includes(newProfileId)) return;

    const newProfile: PerfilRelatorioConfig = {
      nome: newProfileId.charAt(0).toUpperCase() + newProfileId.slice(1),
      descricao_resumida: 'Descrição resumida do perfil',
      descricao_completa: 'Descrição completa do perfil',
      pontos_fortes: [],
      areas_desenvolvimento: [],
      plano_acao: {
        '30_dias': [],
        '90_dias': [],
        '6_meses': [],
      },
      estrategias_especificas: {
        networking: [],
        apresentacoes: [],
        negociacoes: [],
        lideranca: [],
      },
    };

    updateReportContents({
      ...reportContents,
      perfis: {
        ...reportContents.perfis,
        [newProfileId]: newProfile,
      },
    });

    setSelectedProfile(newProfileId);
  };

  const handleDeleteProfile = (profileId: string) => {
    if (!confirm(`Tem certeza que deseja excluir o perfil "${profileId}"?`)) return;

    const newPerfis = { ...reportContents.perfis };
    delete newPerfis[profileId];

    updateReportContents({
      ...reportContents,
      perfis: newPerfis,
    });

    if (selectedProfile === profileId) {
      setSelectedProfile('');
    }
  };

  const updateCurrentProfile = (updates: Partial<PerfilRelatorioConfig>) => {
    if (!selectedProfile || !currentProfile) return;

    updateReportContents({
      ...reportContents,
      perfis: {
        ...reportContents.perfis,
        [selectedProfile]: { ...currentProfile, ...updates },
      },
    });
  };

  // Strengths management
  const handleAddStrength = () => {
    if (!currentProfile) return;

    const newStrength: PontoForteConfig = {
      titulo: 'Novo Ponto Forte',
      descricao: 'Descrição do ponto forte',
      exemplo_pratico: 'Exemplo prático de aplicação',
    };

    updateCurrentProfile({
      pontos_fortes: [...currentProfile.pontos_fortes, newStrength],
    });
  };

  const handleUpdateStrength = (index: number, updates: Partial<PontoForteConfig>) => {
    if (!currentProfile) return;

    const newStrengths = [...currentProfile.pontos_fortes];
    newStrengths[index] = { ...newStrengths[index], ...updates };

    updateCurrentProfile({
      pontos_fortes: newStrengths,
    });
  };

  const handleDeleteStrength = (index: number) => {
    if (!currentProfile) return;

    updateCurrentProfile({
      pontos_fortes: currentProfile.pontos_fortes.filter((_, i) => i !== index),
    });
  };

  // Development areas management
  const handleAddDevelopmentArea = () => {
    if (!currentProfile) return;

    const newArea: AreaDesenvolvimentoConfig = {
      titulo: 'Nova Área de Desenvolvimento',
      descricao: 'Descrição da área',
      impacto: 'Impacto do desenvolvimento desta área',
      sugestoes: ['Sugestão 1', 'Sugestão 2'],
    };

    updateCurrentProfile({
      areas_desenvolvimento: [...currentProfile.areas_desenvolvimento, newArea],
    });
  };

  const handleUpdateDevelopmentArea = (index: number, updates: Partial<AreaDesenvolvimentoConfig>) => {
    if (!currentProfile) return;

    const newAreas = [...currentProfile.areas_desenvolvimento];
    newAreas[index] = { ...newAreas[index], ...updates };

    updateCurrentProfile({
      areas_desenvolvimento: newAreas,
    });
  };

  const handleDeleteDevelopmentArea = (index: number) => {
    if (!currentProfile) return;

    updateCurrentProfile({
      areas_desenvolvimento: currentProfile.areas_desenvolvimento.filter((_, i) => i !== index),
    });
  };

  // Action plan management
  const handleAddPlanTask = (period: PlanPeriod) => {
    if (!currentProfile) return;

    const newTask: TarefaPlanoAcaoConfig = {
      tarefa: 'Nova tarefa',
      descricao: 'Descrição da tarefa',
      area_melhoria: 'Área de melhoria',
      complexidade: 'baixa',
    };

    const currentPlan = currentProfile.plano_acao[period] || [];
    
    updateCurrentProfile({
      plano_acao: {
        ...currentProfile.plano_acao,
        [period]: [...currentPlan, newTask],
      },
    });
  };

  const handleUpdatePlanTask = (period: PlanPeriod, index: number, updates: Partial<TarefaPlanoAcaoConfig>) => {
    if (!currentProfile) return;

    const currentPlan = [...(currentProfile.plano_acao[period] || [])];
    currentPlan[index] = { ...currentPlan[index], ...updates };

    updateCurrentProfile({
      plano_acao: {
        ...currentProfile.plano_acao,
        [period]: currentPlan,
      },
    });
  };

  const handleDeletePlanTask = (period: PlanPeriod, index: number) => {
    if (!currentProfile) return;

    const currentPlan = currentProfile.plano_acao[period] || [];
    
    updateCurrentProfile({
      plano_acao: {
        ...currentProfile.plano_acao,
        [period]: currentPlan.filter((_, i) => i !== index),
      },
    });
  };

  const handleMovePlanTask = (period: PlanPeriod, index: number, direction: 'up' | 'down') => {
    if (!currentProfile) return;

    const currentPlan = [...(currentProfile.plano_acao[period] || [])];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    
    if (newIndex < 0 || newIndex >= currentPlan.length) return;

    [currentPlan[index], currentPlan[newIndex]] = [currentPlan[newIndex], currentPlan[index]];

    updateCurrentProfile({
      plano_acao: {
        ...currentProfile.plano_acao,
        [period]: currentPlan,
      },
    });
  };

  // Strategies management
  const handleAddStrategy = (category: string) => {
    if (!currentProfile) return;

    const newStrategy = prompt(`Nova estratégia para ${category}:`);
    if (!newStrategy) return;

    const currentStrategies = currentProfile.estrategias_especificas[category] || [];

    updateCurrentProfile({
      estrategias_especificas: {
        ...currentProfile.estrategias_especificas,
        [category]: [...currentStrategies, newStrategy],
      },
    });
  };

  const handleUpdateStrategy = (category: string, index: number, value: string) => {
    if (!currentProfile) return;

    const currentStrategies = [...(currentProfile.estrategias_especificas[category] || [])];
    currentStrategies[index] = value;

    updateCurrentProfile({
      estrategias_especificas: {
        ...currentProfile.estrategias_especificas,
        [category]: currentStrategies,
      },
    });
  };

  const handleDeleteStrategy = (category: string, index: number) => {
    if (!currentProfile) return;

    const currentStrategies = currentProfile.estrategias_especificas[category] || [];

    updateCurrentProfile({
      estrategias_especificas: {
        ...currentProfile.estrategias_especificas,
        [category]: currentStrategies.filter((_, i) => i !== index),
      },
    });
  };

  return (
    <div className="space-y-6">
      {/* Profile Selection */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">
            Perfis de Comunicação ({profiles.length})
          </h3>
          <button
            onClick={handleCreateProfile}
            className="inline-flex items-center gap-2 px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <PlusIcon className="w-4 h-4" />
            Novo Perfil
          </button>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {profiles.map((profileId) => (
            <button
              key={profileId}
              onClick={() => setSelectedProfile(profileId)}
              className={`p-3 text-left border-2 rounded-lg transition-colors ${
                selectedProfile === profileId
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
              }`}
            >
              <div className="flex items-center gap-2 mb-1">
                <UserIcon className="w-4 h-4 text-gray-400" />
                <span className="font-medium text-sm">{reportContents.perfis[profileId]?.nome}</span>
              </div>
              <p className="text-xs text-gray-600 line-clamp-2">
                {reportContents.perfis[profileId]?.descricao_resumida}
              </p>
            </button>
          ))}
        </div>
      </div>

      {/* Profile Editor */}
      {currentProfile && (
        <div className="space-y-6">
          {/* Basic Information */}
          <div className="bg-white rounded-lg shadow p-6">
            <h4 className="text-lg font-semibold text-gray-900 mb-4">
              Informações Básicas - {currentProfile.nome}
            </h4>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nome do Perfil
                </label>
                <input
                  type="text"
                  value={currentProfile.nome}
                  onChange={(e) => updateCurrentProfile({ nome: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Descrição Resumida
                </label>
                <textarea
                  value={currentProfile.descricao_resumida}
                  onChange={(e) => updateCurrentProfile({ descricao_resumida: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Descrição Completa
                </label>
                <textarea
                  value={currentProfile.descricao_completa}
                  onChange={(e) => updateCurrentProfile({ descricao_completa: e.target.value })}
                  rows={5}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            <div className="flex justify-end mt-4">
              <button
                onClick={() => handleDeleteProfile(selectedProfile)}
                className="px-3 py-1.5 text-sm text-red-600 border border-red-300 rounded-lg hover:bg-red-50 transition-colors"
              >
                <TrashIcon className="w-4 h-4 inline mr-1" />
                Excluir Perfil
              </button>
            </div>
          </div>

          {/* Strengths */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <StarIcon className="w-5 h-5 text-yellow-500" />
                Pontos Fortes ({currentProfile.pontos_fortes.length})
              </h4>
              <button
                onClick={handleAddStrength}
                className="inline-flex items-center gap-2 px-3 py-1.5 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                <PlusIcon className="w-4 h-4" />
                Adicionar
              </button>
            </div>

            <div className="space-y-4">
              {currentProfile.pontos_fortes.map((strength, index) => (
                <div key={index} className="border border-gray-200 rounded-lg p-4">
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Título
                      </label>
                      <input
                        type="text"
                        value={strength.titulo}
                        onChange={(e) => handleUpdateStrength(index, { titulo: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Descrição
                      </label>
                      <textarea
                        value={strength.descricao}
                        onChange={(e) => handleUpdateStrength(index, { descricao: e.target.value })}
                        rows={2}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Exemplo Prático
                      </label>
                      <textarea
                        value={strength.exemplo_pratico}
                        onChange={(e) => handleUpdateStrength(index, { exemplo_pratico: e.target.value })}
                        rows={2}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                      />
                    </div>

                    <div className="flex justify-end">
                      <button
                        onClick={() => handleDeleteStrength(index)}
                        className="p-1.5 text-red-600 hover:bg-red-50 rounded transition-colors"
                      >
                        <TrashIcon className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Development Areas */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <ArrowTrendingUpIcon className="w-5 h-5 text-blue-500" />
                Áreas de Desenvolvimento ({currentProfile.areas_desenvolvimento.length})
              </h4>
              <button
                onClick={handleAddDevelopmentArea}
                className="inline-flex items-center gap-2 px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <PlusIcon className="w-4 h-4" />
                Adicionar
              </button>
            </div>

            <div className="space-y-4">
              {currentProfile.areas_desenvolvimento.map((area, index) => (
                <div key={index} className="border border-gray-200 rounded-lg p-4">
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Título
                      </label>
                      <input
                        type="text"
                        value={area.titulo}
                        onChange={(e) => handleUpdateDevelopmentArea(index, { titulo: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Descrição
                      </label>
                      <textarea
                        value={area.descricao}
                        onChange={(e) => handleUpdateDevelopmentArea(index, { descricao: e.target.value })}
                        rows={2}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Impacto
                      </label>
                      <textarea
                        value={area.impacto}
                        onChange={(e) => handleUpdateDevelopmentArea(index, { impacto: e.target.value })}
                        rows={2}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Sugestões (uma por linha)
                      </label>
                      <textarea
                        value={area.sugestoes.join('\n')}
                        onChange={(e) => handleUpdateDevelopmentArea(index, { 
                          sugestoes: e.target.value.split('\n').filter(s => s.trim()) 
                        })}
                        rows={3}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                        placeholder="Digite uma sugestão por linha"
                      />
                    </div>

                    <div className="flex justify-end">
                      <button
                        onClick={() => handleDeleteDevelopmentArea(index)}
                        className="p-1.5 text-red-600 hover:bg-red-50 rounded transition-colors"
                      >
                        <TrashIcon className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Action Plan */}
          <div className="bg-white rounded-lg shadow p-6">
            <h4 className="text-lg font-semibold text-gray-900 flex items-center gap-2 mb-4">
              <CalendarDaysIcon className="w-5 h-5 text-purple-500" />
              Plano de Ação
            </h4>

            {/* Period Tabs */}
            <div className="flex gap-1 mb-4 bg-gray-100 p-1 rounded-lg">
              {(Object.keys(planPeriodLabels) as PlanPeriod[]).map((period) => (
                <button
                  key={period}
                  onClick={() => setActivePlanPeriod(period)}
                  className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                    activePlanPeriod === period
                      ? 'bg-white text-purple-700 shadow-sm'
                      : 'text-gray-600 hover:text-purple-600'
                  }`}
                >
                  {planPeriodLabels[period]} ({(currentProfile.plano_acao[period] || []).length})
                </button>
              ))}
            </div>

            {/* Tasks for Active Period */}
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h5 className="font-medium text-gray-900">
                  Tarefas - {planPeriodLabels[activePlanPeriod]}
                </h5>
                <button
                  onClick={() => handleAddPlanTask(activePlanPeriod)}
                  className="inline-flex items-center gap-2 px-3 py-1.5 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                >
                  <PlusIcon className="w-4 h-4" />
                  Adicionar Tarefa
                </button>
              </div>

              {(currentProfile.plano_acao[activePlanPeriod] || []).map((task, index) => (
                <div key={index} className="border border-gray-200 rounded-lg p-4">
                  <div className="space-y-3">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Tarefa
                        </label>
                        <input
                          type="text"
                          value={task.tarefa}
                          onChange={(e) => handleUpdatePlanTask(activePlanPeriod, index, { tarefa: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Área de Melhoria
                        </label>
                        <input
                          type="text"
                          value={task.area_melhoria}
                          onChange={(e) => handleUpdatePlanTask(activePlanPeriod, index, { area_melhoria: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Descrição
                      </label>
                      <textarea
                        value={task.descricao}
                        onChange={(e) => handleUpdatePlanTask(activePlanPeriod, index, { descricao: e.target.value })}
                        rows={2}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                      />
                    </div>

                    <div className="flex items-center justify-between">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Complexidade
                        </label>
                        <select
                          value={task.complexidade}
                          onChange={(e) => handleUpdatePlanTask(activePlanPeriod, index, { complexidade: e.target.value })}
                          className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                        >
                          <option value="baixa">Baixa</option>
                          <option value="media">Média</option>
                          <option value="alta">Alta</option>
                        </select>
                      </div>

                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => handleMovePlanTask(activePlanPeriod, index, 'up')}
                          disabled={index === 0}
                          className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          <ArrowUpIcon className="w-4 h-4" />
                        </button>
                        
                        <button
                          onClick={() => handleMovePlanTask(activePlanPeriod, index, 'down')}
                          disabled={index === (currentProfile.plano_acao[activePlanPeriod] || []).length - 1}
                          className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          <ArrowDownIcon className="w-4 h-4" />
                        </button>

                        <button
                          onClick={() => handleDeletePlanTask(activePlanPeriod, index)}
                          className="p-1.5 text-red-600 hover:bg-red-50 rounded transition-colors"
                        >
                          <TrashIcon className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Strategies */}
          <div className="bg-white rounded-lg shadow p-6">
            <h4 className="text-lg font-semibold text-gray-900 flex items-center gap-2 mb-4">
              <LightBulbIcon className="w-5 h-5 text-orange-500" />
              Estratégias Específicas
            </h4>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {Object.entries(currentProfile.estrategias_especificas).map(([category, strategies]) => (
                <div key={category} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h5 className="font-medium text-gray-900 capitalize">
                      {category} ({strategies.length})
                    </h5>
                    <button
                      onClick={() => handleAddStrategy(category)}
                      className="p-1.5 text-blue-600 hover:bg-blue-50 rounded transition-colors"
                    >
                      <PlusIcon className="w-4 h-4" />
                    </button>
                  </div>

                  <div className="space-y-2">
                    {strategies.map((strategy, index) => (
                      <div key={index} className="flex items-start gap-2">
                        <textarea
                          value={strategy}
                          onChange={(e) => handleUpdateStrategy(category, index, e.target.value)}
                          rows={2}
                          className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                        />
                        <button
                          onClick={() => handleDeleteStrategy(category, index)}
                          className="p-1 text-red-600 hover:bg-red-50 rounded transition-colors"
                        >
                          <TrashIcon className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Empty State */}
      {!selectedProfile && (
        <div className="text-center py-12">
          <UserIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Selecione um perfil para editar
          </h3>
          <p className="text-gray-600 mb-4">
            Escolha um perfil existente ou crie um novo perfil de comunicação.
          </p>
          <button
            onClick={handleCreateProfile}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <PlusIcon className="w-4 h-4" />
            Criar Novo Perfil
          </button>
        </div>
      )}

      {/* Validation Errors */}
      {validationErrors.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <h4 className="text-sm font-medium text-red-800 mb-2">
            Erros de Validação:
          </h4>
          <ul className="text-sm text-red-700 space-y-1">
            {validationErrors.map((error, index) => (
              <li key={index}>• {error.message}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};